import { useTranslation } from "react-i18next";
import { Apple, Download, Play, Smartphone } from "lucide-react";

// Deep import, not the `@shared/hooks` barrel: the barrel re-exports every
// shared hook, which drags the whole directory into the landing type-check
// project for one config reader.
import { useMobileAppConfig } from "@shared/hooks/use-mobile-app-config";
import { Button } from "@shared/ui/button";

import { localizedHref, resolveLang } from "../lib/links";

/**
 * Landing-page "Get the app" section.
 *
 * Behaviour is driven by the env-controlled, SSR-injected mobile config
 * (`useMobileAppConfig`):
 *   - Android not published → direct APK download (sideload) + "coming soon to
 *     Google Play" note.
 *   - Android published      → "Get it on Google Play" button → store URL.
 *   - iOS not published      → "coming soon to the App Store" note (iOS apps
 *     cannot be sideloaded from the web).
 *   - iOS published          → "Download on the App Store" button → store URL.
 */
export function GetTheAppSection() {
  const { t, i18n } = useTranslation("landing");
  const lang = resolveLang(i18n.resolvedLanguage);
  const { androidPublished, iosPublished, storeUrlAndroid, storeUrlIos, androidDownloadUrl } =
    useMobileAppConfig();

  return (
    <section className="bg-background px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="mx-auto max-w-5xl rounded-[2rem] border border-border bg-muted/40 p-8 text-center sm:p-12">
        <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
          {t("getApp.eyebrow")}
        </p>
        <h2 className="mt-3 font-serif text-3xl font-semibold leading-[1.1] tracking-[-0.03em] sm:text-4xl">
          {t("getApp.heading")}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-muted-foreground">
          {t("getApp.subheading")}
        </p>

        {/* Native builds are not on the stores yet. Say so plainly, and point at
            the thing that already works today (the installable web app). The
            line disappears on its own once both platforms are published — it is
            driven by the same env-controlled config as the buttons below. */}
        {(!androidPublished || !iosPublished) && (
          <p
            data-testid="get-app-in-progress"
            className="mx-auto mt-4 flex max-w-2xl items-center justify-center gap-2 text-sm text-muted-foreground"
          >
            <Smartphone className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t("getApp.inProgress")}
          </p>
        )}

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {/* Android */}
          {androidPublished && storeUrlAndroid ? (
            <Button size="lg" asChild>
              <a href={storeUrlAndroid} target="_blank" rel="noopener noreferrer">
                <Play className="h-4 w-4" aria-hidden="true" />
                {t("getApp.android.storeBadge")}
              </a>
            </Button>
          ) : (
            <Button size="lg" asChild>
              {/* Direct APK download — served by the downloads app's active release. */}
              <a href={androidDownloadUrl} download>
                <Download className="h-4 w-4" aria-hidden="true" />
                {t("getApp.android.directDownload")}
              </a>
            </Button>
          )}

          {/* iOS */}
          {iosPublished && storeUrlIos ? (
            <Button size="lg" variant="outline" asChild>
              <a href={storeUrlIos} target="_blank" rel="noopener noreferrer">
                <Apple className="h-4 w-4" aria-hidden="true" />
                {t("getApp.ios.storeBadge")}
              </a>
            </Button>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-md border border-dashed border-border px-4 py-2 text-sm text-muted-foreground">
              <Apple className="h-4 w-4" aria-hidden="true" />
              {t("getApp.ios.comingSoon")}
            </span>
          )}
        </div>

        {/* Pre-launch status note + link to the full download page. */}
        <p className="mt-5 text-sm text-muted-foreground">
          {!androidPublished && (
            <span className="inline-flex items-center gap-1">
              <Smartphone className="h-3.5 w-3.5" aria-hidden="true" />
              {t("getApp.android.comingSoonStore")}
            </span>
          )}
        </p>
        <p className="mt-2 text-sm">
          <a
            className="font-medium text-primary underline-offset-4 hover:underline"
            href={localizedHref("/download", lang)}
          >
            {t("getApp.allOptions")}
          </a>
        </p>
      </div>
    </section>
  );
}

export default GetTheAppSection;
